import * as THREE from 'three'
import _ from 'lodash'
import { round } from 'mathjs'

import Config from '@glass-designer/config'
import GlassSegment from '@/model/glassSegment'
import MathsUtils from '@/utils/mathsUtils'

import Types, { SegmentTypes } from '@glass-designer/types'

/**
 * 2D Clipping functions
 * Provides ability to clip a subject shape with a clipping shape
 * Both shapes defined by GlassSegment arrays
 * This has been coded (instead of a 3rd party library) to help retain all the GlassSegment details
 * And retain the arc glass segment types
 * 
 * Based on entry/exit clipping algorithm from:
 * https://davis.wpi.edu/~matt/courses/clipping/
 * https://davis.wpi.edu/~matt/courses/clipping/code.html
 */
export default class EditorClipper {
  constructor() {
    this.curNodeId = 1
    this.EPSILON = 0.0000001
  }


  /**
   * Performs as boolean clipping operation on a segment list with another source segment list
   * Operations are either union (add) or difference (subtract)
   * Returns the resulting solution as a new segment list
   * 
   * Rules:
   * 1) Clipping shape cannot be entirely inside or outside the shape. They must intersect the edges. Center cutouts are used for internal holes
   * 2) The clipping operation cannot create multiple paths when splitting the subject path
   * 3) Paths must be closed
   * 
   * 
   * @param {Array<GlassSegment>} subjectSegmentList - Subject shape being clipped
   * @param {Array<GlassSegment>} clipSegmentList - Shape clipping the subject shape
   * @param {Boolean} isAdditiveOp - Additive Operation Flag
   *    If true, will add the shapes in a union operation
   *    If false, will remove the clip shape from subject in a difference operation
   * @return {Object} Returns an object contains results of the clipping. Including properties:
   *    segmentList - Array of GlassSegments of the clipping result
   *    startPosition - Vector2 of the start point of the clipping on the subject list
   *    endPosition - Vector2 of the end point of the clipping on the subject list
   */
  performSegmentListClippingOperation(subjectSegmentList, clipSegmentList, isAdditiveOp = false) {
    var subjectPairList = []
    var clipPairList = []

    // Build the node lists
    var subjectNodeList = this._buildShapeNodes(subjectSegmentList, subjectPairList)
    var clipNodeList = this._buildShapeNodes(clipSegmentList, clipPairList)

    return this._performInternalClippingOperation(
      subjectSegmentList,
      clipSegmentList, 
      subjectNodeList,
      clipNodeList,
      subjectPairList,
      clipPairList,
      isAdditiveOp,
      false
    )    
  }


  /**
   * Performs a clipping operation on a segment list using a clipping line
   * Operation is always subtract and will use the inside of the clipping line
   * 
   * @param {Array<GlassSegment>} subjectSegmentList - Subject shape being clipped
   * @param {Vector2} clipStartPoint - Start point of the clipping line
   * @param {Vector2} clipEndPoint - End point of the clipping line
   * @return {Array<Object>} Returns an object contains results of the clipping. Including properties:
   *    segmentList - Array of GlassSegments of the clipping result
   *    startPosition - Vector2 of the start point of the clipping on the subject list
   *    endPosition - Vector2 of the end point of the clipping on the subject list
   */
  performLineClippingOperation(subjectSegmentList, clipStartPoint, clipEndPoint) {
    var subjectPairList = []
    var clipPairList = []

    var flipSubjectEntryFlag = false

    // Build the node lists
    var subjectNodeList = this._buildShapeNodes(subjectSegmentList, subjectPairList)
    var clipNodeList = []

    // If the 1st node is on the outside of the clipping line, flip the entry flag
    var clipDir = clipEndPoint.clone().sub(clipStartPoint)
    clipDir.normalize()

    // Extend the clippign start/end points out a little to avoid rounding/precision errors
    clipStartPoint.addScaledVector(clipDir, -0.5)
    clipEndPoint.addScaledVector(clipDir, 0.5)

    let clipRight = new THREE.Vector2(clipDir.y, -clipDir.x)
    if(MathsUtils.calculateDistanceToPlane2D(clipRight, clipStartPoint, subjectNodeList[0].position) < -this.EPSILON) {
      flipSubjectEntryFlag = true
    }

    // Build the clip nodes manually
    var clipSegment = new GlassSegment(Types.SegmentTypes.line, { startPosition: clipStartPoint, endPosition: clipEndPoint })
    var clipSegmentList = [clipSegment]

    var nodeAId = this.curNodeId++
    var nodeBId = this.curNodeId++

    clipNodeList.push({
      id: nodeAId,
      position: this._roundPoint(clipSegment.startPosition),
      arcAngle: 0,
      cornerRadius: 0,
      alpha: 0,
      entry: 0,
      visited: false,
      intersect: false,
      overlap: false,
      neighbourId: 0,
      
      // Segment info
      type: clipSegment.type,
      segmentIndex: 0,
      segmentId: clipSegment.id,
      segmentArcCenterPosition: this._roundPoint(clipSegment.arcCenterPosition),
      segmentArcRadius: 0,
      segmentArcStartAngle: 0,
      segmentArcEndAngle: 0,
      segmentArcClockwise: 0
    })
    clipNodeList.push({
      id: nodeBId,
      position: this._roundPoint(clipSegment.endPosition),
      arcAngle: 0,
      cornerRadius: 0,
      alpha: 0,
      entry: 0,
      visited: false,
      intersect: false,
      overlap: false,
      neighbourId: 0,
      
      // Segment info
      type: clipSegment.type,
      segmentIndex: 1,
      segmentId: clipSegment.id,
      segmentArcCenterPosition: this._roundPoint(clipSegment.arcCenterPosition),
      segmentArcRadius: 0,
      segmentArcStartAngle: 0,
      segmentArcEndAngle: 0,
      segmentArcClockwise: 0
    })

    clipPairList.push({
      nodeAId: nodeAId,
      nodeBId: nodeBId,
      segmentId: clipSegment.id,
      segmentIndex: 0
    })

    return this._performInternalClippingOperation(
      subjectSegmentList,
      clipSegmentList, 
      subjectNodeList,
      clipNodeList,
      subjectPairList,
      clipPairList,
      false,
      flipSubjectEntryFlag
    )
  }


  /**
   * Runs the internal clipping operation using all the calculated info
   * 
   * @param {Array<GlassSegment>} subjectSegmentList - Subject shape being clipped
   * @param {Array<GlassSegment>} clipSegmentList - Shape clipping the subject shape
   * @param {Array<Object>} subjectNodeList - Node list for the subject shape
   * @param {Array<Object>} clipNodeList - Node list for the clip shape
   * @param {Array<Object>} subjectPairList - Pair info for the subject shape
   * @param {Array<Object>} clipPairList - Pair info for the subject shape
   * @param {Boolean} isAdditiveOp - Additive Operation Flag
   *    If true, will add the shapes in a union operation
   *    If false, will remove the clip shape from subject in a difference operation
   * @param {Boolean} flipSubjectEntryFlag - Flip subject entry flag. Used internally to start in subject or clip
   * @return {Array<Object>} Returns an object contains results of the clipping. Including properties:
   *    segmentList - Array of GlassSegments of the clipping result
   *    startPosition - Vector2 of the start point of the clipping on the subject list
   *    endPosition - Vector2 of the end point of the clipping on the subject list
   */
  _performInternalClippingOperation(
    subjectSegmentList,
    clipSegmentList,
    subjectNodeList,
    clipNodeList, 
    subjectPairList,
    clipPairList,
    isAdditiveOp,
    flipSubjectEntryFlag
  ) {

    var safetyCheck = 3500

    // Build intersections
    for(var subIndex=0; subIndex<subjectNodeList.length-1 && safetyCheck-- > 0; subIndex++) {
      var subNode = subjectNodeList[subIndex]
      if(subNode.intersect)
        continue

      for(var clipIndex=0; clipIndex<clipNodeList.length-1 && safetyCheck-- > 0; clipIndex++) {
        var clipNode = clipNodeList[clipIndex]
        if(clipNode.intersect)
          continue

        var intInfoList = []
        let curSubNode = subjectNodeList[subIndex]
        let nextSubNode = this._nextNode(subjectNodeList, subIndex+1)
        let curClipNode = clipNodeList[clipIndex]
        let nextClipNode = this._nextNode(clipNodeList, clipIndex+1)

        if(!nextSubNode || !nextClipNode) {
          console.error('[CLIP] Failed to get next node')
          continue
        }

        if(this._checkNodeIntersection(curSubNode, nextSubNode, curClipNode, nextClipNode, intInfoList)) {

          intInfoList.forEach(intInfo => {

            // If intersection already in the list, ignore this
            let existingSubNode = this._findIntersectionNode(subjectNodeList, subjectNodeList[subIndex], intInfo.intersection)
            let existingClipNode = this._findIntersectionNode(clipNodeList, clipNodeList[clipIndex], intInfo.intersection)

            if(existingSubNode || existingClipNode)
              return

            // Insert intersection nodes
            let subNodeId = this._insertNode(subjectNodeList, subIndex, subjectPairList, {
              position: intInfo.intersection,
              alpha: intInfo.subAlpha,
              arcAngle: intInfo.subArcAngle,
              cornerRadius: intInfo.cornerRadius,
              entry: 0,
              visited: false,
              intersect: true
            })

            let clipNodeId = this._insertNode(clipNodeList, clipIndex, clipPairList, {
              position: intInfo.intersection,
              alpha: intInfo.clipAlpha,
              arcAngle: intInfo.clipArcAngle,
              cornerRadius: intInfo.cornerRadius,
              entry: 0,
              visited: false,
              intersect: true,
            })

            // Link to each other
            this._findNodeById(subjectNodeList, subNodeId).neighbourId = clipNodeId
            this._findNodeById(clipNodeList, clipNodeId).neighbourId = subNodeId
          })
        }
      }
    }

    if(safetyCheck <= 0) {
      console.log('[CLIP] Intersection count failed')
      return []
    }

    // TODO: Check if intersection points on subject rest of a segment start & endpoint
    // TODO: Label nodes as overlap for degenerate cases
    // TODO: If it's an overlap, don't insert the intersection point

   
    // Convert node list into raw point list
    // Converts arcs into subdivided points for the entry/exit tests
    let subPointList = this._subdivideNodeListToPoints(subjectNodeList)
    let clipPointList = this._subdivideNodeListToPoints(clipNodeList)

    // Run test between subject & clipping node lists
    // Setup entry exit flags
    var entryFlag = this._testNodeList(subjectNodeList, clipPointList, clipNodeList)

    // Bit Hacky: If this is a clipping line, the points are always intersecting
    if(clipSegmentList.length == 1)
      entryFlag = 1

    if(flipSubjectEntryFlag)
      entryFlag = !entryFlag
    this._flagEntryExitNodes(subjectNodeList, entryFlag)

    //this._debugOutputNodeList('subject', subjectNodeList)

    entryFlag = this._testNodeList(clipNodeList, subPointList, subjectNodeList)
    if(!isAdditiveOp)
      entryFlag = !entryFlag

    if(clipSegmentList.length == 1)
      entryFlag = 1

    this._flagEntryExitNodes(clipNodeList, entryFlag)

    //this._debugOutputNodeList('clip', clipNodeList)

    var resultList = []

    // Perform the solution using entry/exit flags to generate the resulting node list
    var solutionNode = null
    var startNodeId = subjectNodeList[0].id
    safetyCheck = 3500
    var isSubjectList = true
    var prevSolNode = null
    while((solutionNode = this._firstUnvisitedNode(subjectNodeList)) && solutionNode.id != startNodeId && safetyCheck-- > 0) {

      var resultNodeList = []

      while(!solutionNode.visited && safetyCheck-- > 0) {

        // Create the polygon
        var forward = solutionNode.entry
        while(safetyCheck-- > 0) {
          let newNode = {
            id: this.curNodeId++,
            position: solutionNode.position.clone(),
            arcAngle: solutionNode.arcAngle,
            cornerRadius: solutionNode.cornerRadius,
            alpha: 0,
            entry: solutionNode.entry,
            visited: false,
            intersect: solutionNode.intersect,   // Copy intersect value for outputting clipping info
            overlap: solutionNode.overlap,
            neighbourId: 0,
            subjectSourceNodeId: isSubjectList ? solutionNode.id : solutionNode.neighbourId,
            clipSourceNodeId: isSubjectList ? solutionNode.neighbourId : solutionNode.id,
            isSubjectList: isSubjectList,
            solutionNodeId: solutionNode.id,
            prevNodeId: prevSolNode ? prevSolNode.id : 0,
            
            // Segment info
            type: solutionNode.type,
            segmentIndex: solutionNode.segmentIndex,
            segmentArcCenterPosition: solutionNode.segmentArcCenterPosition ? solutionNode.segmentArcCenterPosition.clone() : null,
            segmentArcRadius: solutionNode.segmentArcRadius,
            segmentArcStartAngle: solutionNode.segmentArcStartAngle,
            segmentArcEndAngle: solutionNode.segmentArcEndAngle,
            segmentArcClockwise: solutionNode.segmentArcClockwise
          }

          prevSolNode = solutionNode

          resultNodeList.splice(0, 0, newNode)
          solutionNode.visited = true

          // Jump to next/back node in the list
          let curIndex = this._findNodeIndexById(isSubjectList ? subjectNodeList : clipNodeList, solutionNode.id)
          if(curIndex == -1) {
            console.error('ERROR: Solution node not found')
            return []
          }
          solutionNode = this._getNodeByIndex(isSubjectList ? subjectNodeList : clipNodeList, forward ? curIndex+1 : curIndex-1)

          if(solutionNode.intersect) {
            solutionNode.visited = true

            // Jump to neighbouring node in opposite list
            isSubjectList = !isSubjectList
            solutionNode = this._findNodeById(isSubjectList ? subjectNodeList : clipNodeList, solutionNode.neighbourId)
            if(!solutionNode) {
              console.error('ERROR: Neighbouring node not found')
              return []
            }
            
            break
          }
        }
      }

      if(resultNodeList.length > 0)
        resultList.push(resultNodeList)

      // We only support 1 polygon result
      break
    }

    if(safetyCheck <= 0)
      return []

    var clipStartPosition = new THREE.Vector2()
    var clipEndPosition = new THREE.Vector2()

    // Generate the new segment list based on the results
    var resultSegmentList = []
    resultList.forEach(nodeList => {
      var nodeIndex = 0

      // Ensure the node list is closed
      // Note: Do NOT clean, we'll just ignore zero length pairs
      // Otherwise we'll remove nodes & lose pair links
      this._closeNodeList(nodeList)

      for(nodeIndex=0; nodeIndex<nodeList.length-1; nodeIndex++) {
        var node = nodeList[nodeIndex]
        var nextNode = nodeList[nodeIndex+1]

        // Find pair
        var pair = null
        var pairReversed = false
        var pairSubject = true
        if(!nextNode.isSubjectList) {
          pair = this._findPair(clipPairList, node.clipSourceNodeId, nextNode.clipSourceNodeId)
          if(pair) {
            pairSubject = false
            if(pair.nodeAId != node.clipSourceNodeId)
              pairReversed = true
          }
        }
        if(!pair) {
          pair = this._findPair(subjectPairList, node.subjectSourceNodeId, nextNode.subjectSourceNodeId)
          if(pair) {
            pairSubject = true
            if(pair.nodeAId != node.subjectSourceNodeId)
              pairReversed = true
          }
        }
        if(!pair) {
          // No pair found. Ignore the node
          // Happens if the node list is closed (doesn't need a pair)
          continue
        }

        var nodeA = this._findNodeById(pairSubject ? subjectNodeList : clipNodeList, pairReversed ? pair.nodeBId : pair.nodeAId)
        var nodeB = this._findNodeById(pairSubject ? subjectNodeList : clipNodeList, pairReversed ? pair.nodeAId : pair.nodeBId)

        if(!nodeA || !nodeB) {
          console.error(`[CLIP] invalid nodes ${pair.nodeAId} -> ${pair.nodeBId}`)
          continue
        }

        var isSubjectSegment = true
        var pairLinkSegment = subjectSegmentList.find(seg => seg.id == pair.segmentId)
        if(!pairLinkSegment) {
          pairLinkSegment = clipSegmentList.find(seg => seg.id == pair.segmentId)
          isSubjectSegment = false
        }

        if(!pairLinkSegment) {
          console.error(`[CLIP] Could not find source segment from pair ${pair.segmentId}`)
          break
        }

        // Result segment
        // If based on a subject segment, reuse the id & copy details
        let segment = new GlassSegment(pairLinkSegment.type)
        segment.startPosition = nodeA.position.clone()
        segment.endPosition = nodeB.position.clone()

        if(isSubjectSegment) {
          let matchingThreshold = 0.01

          // If segment has same positions, retain the ID
          if((MathsUtils.check2DPointsEqual(segment.startPosition, pairLinkSegment.startPosition, matchingThreshold) && MathsUtils.check2DPointsEqual(segment.endPosition, pairLinkSegment.endPosition, matchingThreshold)) ||
             (MathsUtils.check2DPointsEqual(segment.endPosition, pairLinkSegment.startPosition, matchingThreshold) && MathsUtils.check2DPointsEqual(segment.startPosition, pairLinkSegment.endPosition, matchingThreshold))) {
            segment.id = pairLinkSegment.id
          }

          segment.isArcFromCornerRadius = pairLinkSegment.isArcFromCornerRadius
          segment.isUserFixed = pairLinkSegment.isUserFixed
          segment.isEdgeworkAvailable = pairLinkSegment.isEdgeworkAvailable
          segment.orderEditorEdgeworkConfigId = pairLinkSegment.orderEditorEdgeworkConfigId
          segment.edgeworkSettingValue = pairLinkSegment.edgeworkSettingValue
          segment.isCutoutPolished = pairLinkSegment.isCutoutPolished
          segment.isStartCornerPolished = pairLinkSegment.isStartCornerPolished
          segment.arcEditType = pairLinkSegment.arcEditType
        }

        // Copy arc info
        if(segment.type == Types.SegmentTypes.arc) {
          segment.arcCenterPosition = pairLinkSegment.arcCenterPosition.clone()
          segment.arcRadius = pairLinkSegment.arcRadius
          segment.arcStartAngle = MathsUtils.calculateArcPointAngle(segment.arcCenterPosition, nodeA.position)
          segment.arcEndAngle = MathsUtils.calculateArcPointAngle(segment.arcCenterPosition, nodeB.position)
          segment.arcClockwise = pairLinkSegment.arcClockwise != pairReversed
          segment.arcUpdated()
        }

        // Copy over segment details
        segment.startCornerRadius = nodeA.cornerRadius
        segment.endCornerRadius = nodeB.cornerRadius

        // If the segment is zero length, don't add it
        if(segment.getLength() < 0.00001)
          continue

        resultSegmentList.push(segment)
      }

      // Ensure the segment list is clockwise
      if(MathsUtils.getGlassSegmentListOrientation(resultSegmentList) > 0) {
        resultSegmentList = MathsUtils.reverseGlassSegmentList(resultSegmentList)
      }

      // Calculate the start & end positions of the clipping path in the subject segment list
      let intersectPositions = nodeList.filter(node => node.intersect).map(node => node.position.clone())
      let distanceAlongList = intersectPositions.map(intersectPos => {
        return {
          distance: MathsUtils.calculatePointDistanceAlongSegmentListDistance(resultSegmentList, intersectPos),
          point: intersectPos
        }
      })
      distanceAlongList.sort((distA, distB) => distA.distance - distB.distance)

      // Calculate first & last points
      if(distanceAlongList.length >= 2) {
        let segListLength = MathsUtils.calculateSegmentListLength(resultSegmentList)

        // Setup clipping start/end points
        clipStartPosition.copy(distanceAlongList[0].point)
        clipEndPosition.copy(distanceAlongList[distanceAlongList.length-1].point)

        // Make sure start/end points are correct, otherwise swap to match clockwise orientation of result segments
        let minDist = distanceAlongList[distanceAlongList.length-1].distance - distanceAlongList[0].distance
        minDist = Math.min(minDist, (segListLength - distanceAlongList[distanceAlongList.length-1].distance) + distanceAlongList[0].distance)

        let midDistAlongA = distanceAlongList[0].distance + minDist / 2
        let midDistAlongB = distanceAlongList[distanceAlongList.length-1].distance + minDist / 2

        midDistAlongA = MathsUtils.wrapValue(midDistAlongA, segListLength)
        midDistAlongB = MathsUtils.wrapValue(midDistAlongB, segListLength)

        let midpoint = MathsUtils.calculateLineMidpoint(distanceAlongList[0].point, distanceAlongList[distanceAlongList.length-1].point)

        let pointA = MathsUtils.calculatePointAlongSegmentList(resultSegmentList, midDistAlongA)
        let pointB = MathsUtils.calculatePointAlongSegmentList(resultSegmentList, midDistAlongB)

        if(pointB.distanceTo(midpoint) < pointA.distanceTo(midpoint)) {
          clipStartPosition.copy(distanceAlongList[distanceAlongList.length-1].point)
          clipEndPosition.copy(distanceAlongList[0].point)
        }
      }
    })

    // Make sure the segments are connected without precision errors. Only apply to arcs
    // Happens occasionally with cuts along arcs
    // If we don't do this for arcs getting cut, sometimes the convex hull is not correctly calculated & detects cutouts where none exist
    resultSegmentList.forEach((segment, segmentIndex) => {
      let connectTolerance = 0.1
      let prevSegment = resultSegmentList[segmentIndex == 0 ? resultSegmentList.length-1 : segmentIndex-1]
      let nextSegment = resultSegmentList[segmentIndex == resultSegmentList.length-1 ? 0 : segmentIndex+1]

      if(segment.startPosition.distanceTo(prevSegment.endPosition) < connectTolerance && segment.type == Types.SegmentTypes.arc) {
        segment.startPosition = prevSegment.endPosition
        segment.arcUpdated()
      }
      if(segment.endPosition.distanceTo(nextSegment.startPosition) < connectTolerance && segment.type == Types.SegmentTypes.arc) {
        segment.endPosition = nextSegment.startPosition
        segment.arcUpdated()
      }
    })

    // Recalc info in segment list
    MathsUtils.segmentListUpdated(resultSegmentList)

    return {
      segmentList: resultSegmentList,
      startPosition: clipStartPosition,
      endPosition: clipEndPosition
    }
  }



  /**
   * Run a test between a node list and the other node list converted to points
   * Check if the first node of the first list is inside the shape of the second node list
   */
  _testNodeList(testNodeList, polygonPointList, otherNodeList) {
    if(testNodeList.length == 0 || polygonPointList.length == 0)
      return 0

    var tolerance = 0.0001
    let testNode = testNodeList[0]

    // Edge Case: If exactly on the line, we need to determine if the following nodes is going in or out of the polygon
    for(var i=0; i<otherNodeList.length; i++) {
      let prevNode = otherNodeList[i]
      let nextNode = otherNodeList[(i+1) % otherNodeList.length]
      var inside = false

      if(prevNode.type == Types.SegmentTypes.line) {
        inside = MathsUtils.point2DOnLineSegment(prevNode.position, nextNode.position, testNode.position, tolerance)

      } else if(prevNode.type == Types.SegmentTypes.arc) {
        inside = MathsUtils.point2DOnArc(
          prevNode.segmentArcCenterPosition,
          prevNode.segmentArcRadius,
          MathsUtils.calculateArcPointAngle(prevNode.segmentArcCenterPosition, prevNode.position),
          MathsUtils.calculateArcPointAngle(prevNode.segmentArcCenterPosition, nextNode.position),
          prevNode.segmentArcClockwise,
          testNode.position,
          tolerance
        )
      }

      if(inside) {
        // Find next node & get check midpoint between nodes
        for(var n=1; n<testNodeList.length; n++) {
          let prevTestNode = testNodeList[n-1]
          let nextTestNode = testNodeList[n]
          if(!this._comparePoints(testNode.position, nextTestNode.position)) {
            let midPoint = testNode.position

            if(prevTestNode.type == Types.SegmentTypes.line) {
              midPoint = prevTestNode.position.clone().add(nextTestNode.position).multiplyScalar(0.5)
            } else {
              let arcEllipseCurve = new THREE.EllipseCurve(
                prevTestNode.segmentArcCenterPosition.x,
                prevTestNode.segmentArcCenterPosition.y,
                prevTestNode.segmentArcRadius,
                prevTestNode.segmentArcRadius,
                MathsUtils.degreesToRadians(MathsUtils.calculateArcPointAngle(prevTestNode.segmentArcCenterPosition, prevTestNode.position)),
                MathsUtils.degreesToRadians(MathsUtils.calculateArcPointAngle(prevTestNode.segmentArcCenterPosition, nextTestNode.position)),
                !prevTestNode.segmentArcClockwise,      // NOTE: See notes on top for explanation
                0
              )
              
              midPoint = arcEllipseCurve.getPoint(0.5, new THREE.Vector2())
            }

            // If it was on the boundary but now outside, flag as inside. This way it's available for exit flag
            // Conversely, if on the boundary & now inside, flag as outside. So it'll get entry flag
            inside = !MathsUtils.pointIn2DPolygon(midPoint, polygonPointList)
            break
          }
        }

        return inside
      }
    }

    // Simple node inside the other polygon or not
    return MathsUtils.pointIn2DPolygon(testNodeList[0].position, polygonPointList)
  }


  /**
   * Flag the entry/exit nodes in a list
   * Alternates between entry & exit for each intersection node
   */
  _flagEntryExitNodes(nodeList, initialFlag) {
    for(var i=0; i<nodeList.length-1; i++) {
      if(nodeList[i].intersect) {
        nodeList[i].entry = initialFlag
        initialFlag = !initialFlag        
      }
    }
  }


  /**
   * Subdivide a list of nodes into a list of 2D points
   * Line nodes converted directly into the position
   * Arc nodes subdivided into many points along the arc
   */
   _subdivideNodeListToPoints(nodeList) {
    let shapeDivisions = Config.Settings.arcSubdivisions

     // TODO: Handle corner radiuses?

      var outputShape = new THREE.Shape()

      for(var i=0; i<nodeList.length-1; i++) {
        let node = nodeList[i]
        if(node.intersect)
          continue
        let nextNode = this._nextNode(nodeList, i+1)
        if(!nextNode)
          continue

        // Ignore if same point
        if(this._comparePoints(node.position, nextNode.position))
          continue


        if(node.type == Types.SegmentTypes.line) {
          if(i == 0)
            outputShape.moveTo(node.position.x, node.position.y)
          
          outputShape.lineTo(nextNode.position.x, nextNode.position.y)

        } else if(node.type == Types.SegmentTypes.arc) {
          outputShape.absarc(
            node.segmentArcCenterPosition.x,
            node.segmentArcCenterPosition.y,
            node.segmentArcRadius,
            MathsUtils.degreesToRadians(MathsUtils.calculateArcPointAngle(node.segmentArcCenterPosition, node.position)),
            MathsUtils.degreesToRadians(MathsUtils.calculateArcPointAngle(node.segmentArcCenterPosition, nextNode.position)),
            !node.segmentArcClockwise     // NOTE: See notes in glassSegment.js for explanation
          )
        }
      }

      return outputShape.getPoints(shapeDivisions)
   }


  /**
   * Build the shape nodes (vertices) from a list of GlassSegments of a shape
   * Returns an array of objects containing node details
   */
  _buildShapeNodes(segmentList, pairList) {
    var nodeList = []

    if(segmentList.length < 1)
      return []

    segmentList.forEach((segment, index) => {
      let prevIndex = index == 0 ? segmentList.length - 1 : index - 1

      // If this segment has same start/end points, ignore it
      // Important so the cleanup node list doesn't really do anything & break the pairs
      if(this._comparePoints(this._roundPoint(segment.startPosition), this._roundPoint(segment.endPosition)))
        return

      let node = {
        id: this.curNodeId++,
        position: this._roundPoint(segment.startPosition),
        arcAngle: segment.arcStartAngle,
        cornerRadius: segment.startCornerRadius,
        alpha: 0,
        entry: 0,
        visited: false,
        intersect: false,
        overlap: false,
        neighbourId: 0,
        
        // Segment info
        type: segment.type,
        segmentIndex: index,
        segmentId: segment.id,
        segmentArcCenterPosition: this._roundPoint(segment.arcCenterPosition),
        segmentArcRadius: segment.arcRadius,
        segmentArcStartAngle: segment.arcStartAngle,
        segmentArcEndAngle: segment.arcEndAngle,
        segmentArcClockwise: segment.arcClockwise
      }
      nodeList.push(node)

      pairList.push({
        nodeAId: node.id,
        nodeBId: node.id + 1,   // Important: Assumes the next node is in the list or closed below
        segmentId: segment.id,
        segmentIndex: index
      })
    })

    // Close the shape
    let endNode = _.cloneDeep(nodeList[0])
    endNode.id = this.curNodeId++
    nodeList.push(endNode)

    // Cleanup the node list to remove any duplicates
    this._cleanupNodeList(nodeList)

    return nodeList
  }


  /**
   * Check intersections between 2 segments in the node lists
   * Returns true if intersection occurs & fills in the intersection info object
   */
  _checkNodeIntersection(subNodeA, subNodeB, clipNodeA, clipNodeB, intInfoList) {
    let pointOnLineTolerance = 0.05
    var intPoint = new THREE.Vector2()

    // Arc -> Segment Intersection Check
    if(subNodeA.type == Types.SegmentTypes.arc && clipNodeA.type == Types.SegmentTypes.line) {
      
      // Calculate arc angle of subnodeB
      var nodeBArcAngle = MathsUtils.calculateArcPointAngle(subNodeA.segmentArcCenterPosition, subNodeB.position)

      // Wrap test
      if(Math.abs(nodeBArcAngle - 0) < 0.001 && nodeBArcAngle < subNodeA.arcAngle)
        nodeBArcAngle = 360

      let intersectionList = MathsUtils.checkSegmentArcIntersection(
        clipNodeA.position,
        clipNodeB.position,
        subNodeA.segmentArcCenterPosition,
        subNodeA.segmentArcRadius,
        subNodeA.arcAngle,
        nodeBArcAngle,
        subNodeA.segmentArcClockwise
      )

      // Edge case: If the line is just touching the arc, don't use it for intersections
      // As there will never be a pair for entry -> exit
      if(intersectionList.length == 1) {
        let lineNorm = new THREE.Vector2()
        lineNorm.subVectors(clipNodeB.position, clipNodeA.position)
        lineNorm.normalize()
        lineNorm.set(lineNorm.y, -lineNorm.x) // Rotate 90

        let arcDist = Math.abs(lineNorm.dot(subNodeA.segmentArcCenterPosition) - clipNodeA.position.dot(lineNorm))
        if(Math.abs(arcDist - subNodeA.segmentArcRadius) < 0.001)
          return false
      }

      intersectionList.forEach(int => {
        intPoint = this._roundPoint(int)

        var pointAngle = MathsUtils.calculateArcPointAngle(subNodeA.segmentArcCenterPosition, intPoint)
        var subBArcAngle = MathsUtils.calculateArcPointAngle(subNodeA.segmentArcCenterPosition, subNodeB.position)

        var intInfo = {
          subAlpha: MathsUtils.mapAngleDegreesValue(pointAngle, subNodeA.arcAngle, subBArcAngle, subNodeA.segmentArcClockwise),
          subArcAngle: pointAngle,
          clipAlpha: this._dist(clipNodeA.position.x, clipNodeA.position.y, intPoint.x, intPoint.y) / this._dist(clipNodeA.position.x, clipNodeA.position.y, clipNodeB.position.x, clipNodeB.position.y),
          clipArcAngle: 0,
          intersection: intPoint,
          cornerRadius: 0
        }

        intInfoList.push(intInfo)
      })

      return intersectionList.length > 0
    }

    // Segment -> Arc Intersection Check
    if(subNodeA.type == Types.SegmentTypes.line && clipNodeA.type == Types.SegmentTypes.arc) {
      // Calculate arc angle of clipNodeB
      let nodeBArcAngle = MathsUtils.calculateArcPointAngle(clipNodeA.segmentArcCenterPosition, clipNodeB.position)

      // Wrap test
      if(Math.abs(nodeBArcAngle - 0) < 0.001 && nodeBArcAngle < clipNodeA.arcAngle)
        nodeBArcAngle = 360

      let intersectionList = MathsUtils.checkSegmentArcIntersection(
        subNodeA.position,
        subNodeB.position,
        clipNodeA.segmentArcCenterPosition,
        clipNodeA.segmentArcRadius,
        clipNodeA.arcAngle,
        nodeBArcAngle,
        clipNodeA.segmentArcClockwise
      )

      // Edge case: If the line is just touching the arc, don't use it for intersections
      // As there will never be a pair for entry -> exit
      if(intersectionList.length == 1) {
        let lineNorm = new THREE.Vector2()
        lineNorm.subVectors(subNodeB.position, subNodeA.position)
        lineNorm.normalize()
        lineNorm.set(lineNorm.y, -lineNorm.x) // Rotate 90

        let arcDist = Math.abs(lineNorm.dot(clipNodeA.segmentArcCenterPosition) - subNodeA.position.dot(lineNorm))
        if(Math.abs(arcDist - clipNodeA.segmentArcRadius) < 0.001)
          return false
      }

      intersectionList.forEach(int => {
        intPoint = this._roundPoint(int)

        let angleDist = nodeBArcAngle - clipNodeA.arcAngle
        var pointAngle = MathsUtils.calculateArcPointAngle(clipNodeA.segmentArcCenterPosition, intPoint)

        var clipAlpha = 0
        if(angleDist != 0) {
          clipAlpha = (pointAngle-clipNodeA.arcAngle) / angleDist
        }

        let intInfo = {
          subAlpha: this._dist(subNodeA.position.x, subNodeA.position.y, intPoint.x, intPoint.y) / this._dist(subNodeA.position.x, subNodeA.position.y, subNodeB.position.x, subNodeB.position.y),
          subArcAngle: 0,
          clipAlpha: clipAlpha,
          clipArcAngle: pointAngle,
          intersection: intPoint,
          cornerRadius: 0
        }

        intInfoList.push(intInfo)
      })

      return intersectionList.length > 0      
    }

    // Arc -> Arc Intersection Check
    if(subNodeA.type == Types.SegmentTypes.arc && clipNodeA.type == Types.SegmentTypes.arc) {
      var subBArcAngle = MathsUtils.calculateArcPointAngle(subNodeA.segmentArcCenterPosition, subNodeB.position)
      var clipBArcAngle = MathsUtils.calculateArcPointAngle(clipNodeA.segmentArcCenterPosition, clipNodeB.position)

      if(Math.abs(subBArcAngle - 0) < 0.001 && subBArcAngle < subNodeA.arcAngle)
        subBArcAngle = 360
      if(Math.abs(clipBArcAngle - 0) < 0.001 && clipBArcAngle < clipNodeA.arcAngle)
        clipBArcAngle = 360

      let intersectionList = MathsUtils.checkArcArcIntersection(
        subNodeA.segmentArcCenterPosition, subNodeA.segmentArcRadius, subNodeA.arcAngle, subBArcAngle, subNodeA.segmentArcClockwise,
        clipNodeA.segmentArcCenterPosition, clipNodeA.segmentArcRadius, clipNodeA.arcAngle, clipBArcAngle, clipNodeA.segmentArcClockwise
      )

      intersectionList.forEach(int => {
        intPoint = this._roundPoint(int)

        var pointAngleSub = MathsUtils.calculateArcPointAngle(subNodeA.segmentArcCenterPosition, intPoint)
        var pointAngleClip = MathsUtils.calculateArcPointAngle(clipNodeA.segmentArcCenterPosition, intPoint)

        let intInfo = {
          subAlpha: MathsUtils.mapAngleDegreesValue(pointAngleSub, subNodeA.arcAngle, subBArcAngle, subNodeA.segmentArcClockwise),
          subArcAngle: pointAngleSub,
          clipAlpha: MathsUtils.mapAngleDegreesValue(pointAngleClip, clipNodeA.arcAngle, clipBArcAngle, clipNodeA.segmentArcClockwise),
          clipArcAngle: pointAngleClip,
          intersection: intPoint,
          cornerRadius: 0
        }

        intInfoList.push(intInfo)
      })

      return intersectionList.length > 0      
    }

    
    // Segment -> Segment Intersection Check
    if(subNodeA.type == Types.SegmentTypes.line && clipNodeA.type == Types.SegmentTypes.line) {
      if(MathsUtils.checkSegmentSegmentIntersection(subNodeA.position, subNodeB.position, clipNodeA.position, clipNodeB.position, intPoint)) {
        intPoint = this._roundPoint(intPoint)

        var intInfo = {
          subAlpha: this._dist(subNodeA.position.x, subNodeA.position.y, intPoint.x, intPoint.y) / this._dist(subNodeA.position.x, subNodeA.position.y, subNodeB.position.x, subNodeB.position.y),
          clipAlpha: this._dist(clipNodeA.position.x, clipNodeA.position.y, intPoint.x, intPoint.y) / this._dist(clipNodeA.position.x, clipNodeA.position.y, clipNodeB.position.x, clipNodeB.position.y),
          intersection: intPoint,
          cornerRadius: 0
        }

        intInfoList.push(intInfo)
        
        return true
      } else {
        var intersectionFound = false

        // Check if points rest directly on the segment
        var closestSubPntA = MathsUtils.getClosest2DPointOnLineSegment(subNodeA.position, subNodeB.position, clipNodeA.position)
        var closestSubPntB = MathsUtils.getClosest2DPointOnLineSegment(subNodeA.position, subNodeB.position, clipNodeB.position)

        var closestClipPntA = MathsUtils.getClosest2DPointOnLineSegment(clipNodeA.position, clipNodeB.position, subNodeA.position)
        var closestClipPntB = MathsUtils.getClosest2DPointOnLineSegment(clipNodeA.position, clipNodeB.position, subNodeB.position)

        if(closestSubPntA.distanceTo(clipNodeA.position) < pointOnLineTolerance) {
          closestSubPntA = this._roundPoint(closestSubPntA)
          let intInfo = {
            subAlpha: this._dist(subNodeA.position.x, subNodeA.position.y, closestSubPntA.x, closestSubPntA.y) / this._dist(subNodeA.position.x, subNodeA.position.y, subNodeB.position.x, subNodeB.position.y),
            clipAlpha: this._dist(clipNodeA.position.x, clipNodeA.position.y, closestSubPntA.x, closestSubPntA.y) / this._dist(clipNodeA.position.x, clipNodeA.position.y, clipNodeB.position.x, clipNodeB.position.y),
            intersection: closestSubPntA,
            cornerRadius: 0
          }
          intInfoList.push(intInfo)
          intersectionFound = true
        }
        if(closestSubPntB.distanceTo(clipNodeB.position) < pointOnLineTolerance) {
          closestSubPntB = this._roundPoint(closestSubPntB)
          let intInfo = {
            subAlpha: this._dist(subNodeA.position.x, subNodeA.position.y, closestSubPntB.x, closestSubPntB.y) / this._dist(subNodeA.position.x, subNodeA.position.y, subNodeB.position.x, subNodeB.position.y),
            clipAlpha: this._dist(clipNodeA.position.x, clipNodeA.position.y, closestSubPntB.x, closestSubPntB.y) / this._dist(clipNodeA.position.x, clipNodeA.position.y, clipNodeB.position.x, clipNodeB.position.y),
            intersection: closestSubPntB,
            cornerRadius: 0
          }
          intInfoList.push(intInfo)
          intersectionFound = true
        }

        if(closestClipPntA.distanceTo(subNodeA.position) < pointOnLineTolerance) {
          closestClipPntA = this._roundPoint(closestClipPntA)
          let intInfo = {
            subAlpha: this._dist(subNodeA.position.x, subNodeA.position.y, closestClipPntA.x, closestClipPntA.y) / this._dist(subNodeA.position.x, subNodeA.position.y, subNodeB.position.x, subNodeB.position.y),
            clipAlpha: this._dist(clipNodeA.position.x, clipNodeA.position.y, closestClipPntA.x, closestClipPntA.y) / this._dist(clipNodeA.position.x, clipNodeA.position.y, clipNodeB.position.x, clipNodeB.position.y),
            intersection: closestClipPntA,
            cornerRadius: 0
          }
          intInfoList.push(intInfo)
          intersectionFound = true
        }
        if(closestClipPntB.distanceTo(subNodeB.position) < pointOnLineTolerance) {
          closestClipPntB = this._roundPoint(closestClipPntB)
          let intInfo = {
            subAlpha: this._dist(subNodeA.position.x, subNodeA.position.y, closestClipPntB.x, closestClipPntB.y) / this._dist(subNodeA.position.x, subNodeA.position.y, subNodeB.position.x, subNodeB.position.y),
            clipAlpha: this._dist(clipNodeA.position.x, clipNodeA.position.y, closestClipPntB.x, closestClipPntB.y) / this._dist(clipNodeA.position.x, clipNodeA.position.y, clipNodeB.position.x, clipNodeB.position.y),
            intersection: closestClipPntB,
            cornerRadius: 0
          }
          intInfoList.push(intInfo)
          intersectionFound = true
        }

        return intersectionFound
      }
    }

    return false
  }


  /**
   * Insert a node into a list based on the existing node but with new values
   * If multiple nodes for the same segment, inserts by alpha so alpha is ordered [0 -> 1]
   * Returns the index in the array the new node was added
   */
  _insertNode(nodeList, nodeIndex, pairList, nodeData) {

    let sourceNode = nodeList[nodeIndex]

    // Find suitable position in the list
    for(var i=0; i<nodeList.length; i++) {
      let insertIndex = (nodeIndex + i) % nodeList.length

      if(nodeList[insertIndex].alpha > nodeData.alpha || (!nodeList[insertIndex].intersect && i > 0)) {

        let nodeId = this.curNodeId++

        var prevIndex = insertIndex-1
        var nextIndex = insertIndex
        if(prevIndex < 0)
          prevIndex += nodeList.length
        let prevNode = nodeList[prevIndex]
        let curNode = nodeList[nextIndex]

        // Remove the current pair & add 2 new pairs
        let curPairIndex = pairList.findIndex(pair => pair.nodeAId == prevNode.id && pair.nodeBId == curNode.id)
        if(curPairIndex != -1) {
          pairList.splice(curPairIndex, 1)
        }

        pairList.push({
          nodeAId: prevNode.id,
          nodeBId: nodeId,
          segmentId: sourceNode.segmentId,
          segmentIndex: sourceNode.segmentIndex
        })
        pairList.push({
          nodeAId: nodeId,
          nodeBId: curNode.id,
          segmentId: sourceNode.segmentId,
          segmentIndex: sourceNode.segmentIndex
        })

        nodeList.splice(insertIndex, 0, {
          id: nodeId,
          position: nodeData.position,
          cornerRadius: nodeData.cornerRadius,
          alpha: nodeData.alpha,
          arcAngle: nodeData.arcAngle || 0,
          entry: nodeData.entry,
          visited: nodeData.visited,
          intersect: nodeData.intersect,
          overlap: false,
          neighbourId: 0,
          
          // Copy segment info from source node
          sourceId: sourceNode.id,
          type: sourceNode.type,
          segmentIndex: sourceNode.segmentIndex,
          segmentId: sourceNode.segmentId,
          segmentArcCenterPosition: this._roundPoint(sourceNode.segmentArcCenterPosition),
          segmentArcRadius: sourceNode.segmentArcRadius,
          segmentArcStartAngle: sourceNode.segmentArcStartAngle,
          segmentArcEndAngle: sourceNode.segmentArcEndAngle,
          segmentArcClockwise: sourceNode.segmentArcClockwise
        })

        return nodeId
      }
      if(!nodeList[insertIndex].intersect && i > 0)
        break
    }

    console.log('[CLIP] ERROR: Node not inserted')
    return -1
  }


  /**
   * Find an existing intersection node with matching point in the list
   * Returns the node if found
   * Returns null if not found
   */
  _findIntersectionNode(nodeList, sourceNode, intersectionPoint) {
    return nodeList.find(node => {
      if(!node.intersect)
        return false
      //if(node.segmentId != sourceNode.id)
        //return false

      return this._comparePoints(node.position, intersectionPoint)
    })
  }


  /**
   * Get the next node in the list that has no intersection flagged
   */
  _nextNode(list, index) {
    var safety = list.length + 1
    while(safety-- > 0) {
      let i = index % list.length
      if(!list[i].intersect)
        return list[i]
      index++
    }
    console.error('ERROR: Next node not found')
    return null
  }


  /**
   * Find the first node in the list that has an intersection and not already visited
   * Returns null if no unvisited intersection nodes found in the list
   */
  _firstUnvisitedNode(list) {
    var safety = list.length + 1
    var index = 0
    while(safety-- > 0) {
      let i = index % list.length
      if(list[i].intersect && !list[i].visited)
        return list[i]
      index++
    }

    return null
  }



  /**
   * Find a node by its ID
   * Returns undefined if not found
   */
  _findNodeById(nodeList, id) {
    return nodeList.find(node => node.id == id)
  }


  /**
   * Find a node index by its ID
   * Returns -1 if not found
   */
  _findNodeIndexById(nodeList, id) {
    return nodeList.findIndex(node => node.id == id)
  }


  /**
   * Return a node in a list given its index
   * Wraps around if index if < 0 or >= length
   */
  _getNodeByIndex(nodeList, index) {
    if(index < 0)
      index += nodeList.length
    index %= nodeList.length
    return nodeList[index]
  }


  /**
   * Find a pair given 2 node ids in a list
   * Also checks if the pair exists reversed
   * 
   * Returns the pair object or null if not found
   */
  _findPair(pairList, nodeAId, nodeBId) {
    return pairList.find(pairItem => {
      if(pairItem.nodeAId == nodeAId && pairItem.nodeBId == nodeBId)
        return true
      if(pairItem.nodeAId == nodeBId && pairItem.nodeBId == nodeAId)
        return true
      return false
    })
  }


  /**
   * Measure distance between two 2D points
   * 
   */
  _dist(x1, y1, x2, y2) {
    return Math.sqrt((x2-x1)*(x2-x1) + (y2-y1)*(y2-y1))
  }


  /**
   * Round a 2D point to a couple decimal places to avoid floating point errors
   */
  _roundPoint(point) {
    let places = 2
    return new THREE.Vector2(round(point.x, places), round(point.y, places))
  }


  /**
   * Compares 2 points
   * Returns true if they are the same (eg, within a tolerance)
   */
  _comparePoints(pointA, pointB, threshold = 0.00001) {
    return Math.abs(pointA.x - pointB.x) <= threshold && Math.abs(pointA.y - pointB.y) <= threshold
  }


  /**
   * Cleans up a node list & ensures there are no duplicate points
   * Also ensures it's closed, with the last & first node at matching positions
   * Mutates the list inplace
   */
  _cleanupNodeList(nodeList) {
    for(var nodeIndex=0; nodeIndex<nodeList.length-1; nodeIndex++) {
      let node = nodeList[nodeIndex]
      let nextNode = nodeList[nodeIndex+1]

      if(this._comparePoints(node.position, nextNode.position)) {
        nodeList.splice(nodeIndex+1, 1)
        nodeIndex = -1    // Start again

        continue
      }
    }

    // Ensure the list is closed
    this._closeNodeList(nodeList)
  }


  /**
   * Ensures a node list is closed by makign the last & first nodes match
   * Mutates the list inplace
   */
  _closeNodeList(nodeList) {
    if(nodeList.length >= 2) {
      if(!this._comparePoints(nodeList[0].position, nodeList[nodeList.length-1].position)) {

        // Insert a new node at the end
        var endNode = _.cloneDeep(nodeList[0])
        endNode.id = this.curNodeId++
        nodeList.push(endNode)
      }
    }
  }


  /**
   * Forces a node list to be clockwise
   * If it isn't, reverses the node order to ensure it is
   * Mutates the list inplace
   */
  _forceNodeListClockwise(nodeList) {
    if(nodeList.length <= 2)
      return

    var pointList = nodeList.map(node => node.position.clone())
    
    // Point list cannot be closed for detection
    if(this._comparePoints(pointList[0], pointList[pointList.length-1]))
      pointList.pop()

    if(MathsUtils.get2DPolygonOrientation(pointList) > 0) {
      nodeList.reverse()
    }
  }


  /**
   * Output debug info for a node list
   * Used for testing / development
   */
  _debugOutputNodeList(name, nodeList) {
    console.log(`nodeList: ${name}`)
    nodeList.forEach((node, index) => {
      var desc = ''
      if(node.type == SegmentTypes.arc)
        desc = `, ARC: cw: ${node.segmentArcClockwise}, sa: ${node.segmentArcStartAngle}, ea: ${node.segmentArcEndAngle}`
      console.log(`   [${index}]: id: ${node.id}, position: [${node.position.x},${node.position.y}], alpha: ${node.alpha}, entry: ${node.entry}, intersect: ${node.intersect}, overlap: ${node.overlap} ${desc}`)
    })
  }

}